import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram, ExternalLink } from "lucide-react";
import { SITE, SERVICES, TOWNS } from "@/config/site";
import { trackEvent } from "@/lib/analytics";

export const Footer = () => {
  return (
    <footer data-testid="site-footer" className="bg-brand-black text-white grain-overlay">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img src="/images/logo.png" alt="" aria-hidden="true" width="44" height="44" className="w-10 h-10 object-contain" />
              <span className="font-display font-extrabold text-xl">
                FRELONS<span className="text-brand-gold">50</span><sup className="text-[0.55em] font-bold text-white/60 ml-0.5">™</sup>
              </span>
            </Link>
            <p className="text-white/70 text-sm leading-relaxed">
              FRELONS50 — Intervention contre les frelons, guêpes, rats, souris et autres nuisibles dans la Manche.
            </p>
            <div className="flex gap-3 mt-5">
              {SITE.social.facebook && (
                <a href={SITE.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-10 h-10 rounded-full bg-white/10 hover:bg-brand-gold hover:text-brand-black flex items-center justify-center transition-colors">
                  <Facebook className="w-4 h-4" />
                </a>
              )}
              {SITE.social.instagram && (
                <a href={SITE.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 rounded-full bg-white/10 hover:bg-brand-gold hover:text-brand-black flex items-center justify-center transition-colors">
                  <Instagram className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-display font-bold text-sm uppercase tracking-widest text-brand-gold mb-4">Nos interventions</h3>
            <ul className="space-y-2.5">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/${s.slug}`} className="text-white/70 hover:text-white text-sm transition-colors">{s.nav}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Zone */}
          <div>
            <h3 className="font-display font-bold text-sm uppercase tracking-widest text-brand-gold mb-4">Zone d'intervention</h3>
            <p className="text-white/70 text-sm mb-3">{SITE.zoneShort}</p>
            <p className="text-white/60 text-sm leading-relaxed">{TOWNS.slice(0, 6).join(" · ")}…</p>
            <Link to="/zones-intervention" className="inline-block mt-3 text-brand-gold text-sm font-semibold hover:underline">
              Voir toutes les zones
            </Link>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-display font-bold text-sm uppercase tracking-widest text-brand-gold mb-4">Contact</h3>
            <ul className="space-y-3">
              <li>
                <a href={SITE.phoneHref} onClick={() => trackEvent("click_phone", { location: "footer" })} className="flex items-center gap-3 text-white/80 hover:text-white text-sm">
                  <Phone className="w-4 h-4 text-brand-gold" /> {SITE.phoneDisplay}
                </a>
              </li>
              <li>
                <a href={SITE.emailHref} onClick={() => trackEvent("click_email", { location: "footer" })} className="flex items-center gap-3 text-white/80 hover:text-white text-sm break-all">
                  <Mail className="w-4 h-4 text-brand-gold shrink-0" /> {SITE.email}
                </a>
              </li>
              <li className="flex items-start gap-3 text-white/80 text-sm">
                <MapPin className="w-4 h-4 text-brand-gold mt-0.5 shrink-0" /> {SITE.departement}
              </li>
              {SITE.social.googleBusiness && (
                <li>
                  <a href={SITE.social.googleBusiness} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-brand-gold text-sm font-semibold">
                    Fiche Google Business <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </li>
              )}
            </ul>
            <div className="mt-5 pt-4 border-t border-white/10">
              <p className="text-white/60 text-xs">Horaires réels</p>
              {SITE.hours.map((h) => (
                <p key={h.day} className="text-white/80 text-sm mt-1">{h.day} : {h.value}</p>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/50 text-xs">© {new Date().getFullYear()} FRELONS50. Tous droits réservés.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link to="/mentions-legales" className="text-white/60 hover:text-white text-xs">Mentions légales</Link>
            <Link to="/politique-confidentialite" className="text-white/60 hover:text-white text-xs">Politique de confidentialité</Link>
            <Link to="/gestion-cookies" className="text-white/60 hover:text-white text-xs">Gestion des cookies</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
